import { sortByObjectKey } from '@utils/collections';

import { request } from 'api/request';

export type AttributeType = {
  shortId: string;
  title: string;
  attributeType: AttributeFieldType;
  isActive: boolean;
  options?: string;
  isTravellerAttribute?: boolean;
};

export type AttributeVariable = {
  field: string;
  label: string;
  source: string;
  type: AttributeFieldType;
  parentType?: string;
  tooltip?: string;
};

export type AttributeFieldType =
  | 'date'
  | 'text'
  | 'paragraph'
  | 'user'
  | 'number'
  | 'decimal'
  | 'single_select'
  | 'multi_select'
  | 'phone_number'
  | 'email'
  | 'file'
  | 'file_multi'
  | 'boolean';

export type AttributeTemplate = {
  id: string;
  shortId: string;
  title: string;
  attributeType: AttributeFieldType;
  createdByName?: string;
  dateUpdated: string;
  numJourneys: number;
  options?: string;
};

export type TravellerAttributeTemplate = Omit<AttributeType, 'templateShortId'> & {
  id: string;
  isRequired: boolean;
  templateTitle: string;
  input: 'required' | 'optional' | 'disabled';
  dateUpdated: string;
  createdByName?: string;
  isSystemAttribute: boolean;
  numTravellers?: number;
  isFieldMapped?: boolean;
  order: number;
};

export type AttributeTemplateData = {
  title: string;
  attributeType: AttributeFieldType;
  options?: string;
};

export type TravellerAttributeTemplateData = AttributeTemplateData & {
  isRequired?: boolean;
  isActive?: boolean;
  numTravellers?: number;
  isSystemAttribute?: boolean;
};

export const loadCompanyTravellerAttributeTemplates = async (
  queryString: string = '',
  systemAttributes: boolean = false
) => {
  const search = systemAttributes
    ? `${queryString}${queryString ? '&' : '?'}system-attributes=1`
    : queryString;
  const response = await request.get<TravellerAttributeTemplate[]>(
    `v1/company-travellers/attributes/${search}`
  );
  return response.data;
};

export const updateTravellerAttributeTemplateOrders = async (data: Record<string, number>) => {
  const response = await request.patch('v1/company-travellers/attributes/order/', {
    shortIds: data,
  });
  return response.data;
};

export const loadTemplateTravellerAttributes = async (
  journeyShortId: string,
  queryString: string = ''
) => {
  const response = await request.get<TravellerAttributeTemplate[]>(
    `v1/templates/journey/${journeyShortId}/traveller-attributes/${queryString}`
  );
  return sortByObjectKey(response.data, 'id');
};

export const loadCompanyTravellerAttributeTemplate = async (
  shortId: string = '',
  isSystemAttributeQuery: boolean = false
) => {
  const response = await request.get<TravellerAttributeTemplate>(
    `v1/company-travellers/attributes/${shortId}/${
      isSystemAttributeQuery ? '?system-attributes=1' : ''
    }`
  );
  return response.data;
};

export const createTravellerAttributeTemplate = async (data: TravellerAttributeTemplateData) => {
  const response = await request.post('v1/company-travellers/attributes/', data);
  return response.data;
};

export const updateTravellerAttributeTemplate = async (
  shortId: string,
  data: TravellerAttributeTemplateData
) => {
  const response = await request.patch(`v1/company-travellers/attributes/${shortId}/`, data);
  return response.data;
};

export const duplicateTravellerAttributeTemplate = async (shortId: string) => {
  const response = await request.post(`v1/company-travellers/attributes/${shortId}/duplicate/`);
  return response.data;
};

export const deleteTravellerAttributeTemplate = async (shortId: string) => {
  const response = await request.delete(`v1/company-travellers/attributes/${shortId}/`);
  return response.data;
};

export const loadCompanyTravellerAttributeTemplateVariables = async () => {
  const response = await request.get<AttributeVariable[]>(
    `v1/company-travellers/attributes/variables/`
  );
  return response.data;
};
